import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const DeleteAccountPage = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    const subject = encodeURIComponent('Account & Data Deletion Request');
    const body = encodeURIComponent(
      `Hello,\n\nI would like to request the deletion of my account and all associated data from the Mister Food Safety platform.\n\nRegistered email: ${email}\n\nPlease confirm once the deletion has been completed.\n\nThank you.`
    );
    window.location.href = `mailto:admin@misterfoodsafety.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 5, md: 8 }, bgcolor: 'background.default' }}>
      <Container maxWidth="sm">
        <Stack spacing={4}>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <DeleteForeverIcon sx={{ fontSize: 48, color: 'error.main' }} />
            <Typography variant="h4" fontWeight={700}>Request Account Deletion</Typography>
            <Typography variant="body2" color="text.secondary">
              Mister Food Safety (OPC) Private Limited
            </Typography>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              You can request the deletion of your account and all personal data associated with
              it at any time. Once your request is processed:
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {[
                'Your login credentials and user account will be permanently deleted.',
                'Your name, email address, phone number, and business address will be removed from our systems.',
                'Access to the Mister Food Safety app will be revoked immediately.',
              ].map((item) => (
                <Box component="li" key={item} sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              Deletion requests are processed within <strong>30 days</strong>. You will receive
              a confirmation email once your account has been deleted.
            </Typography>
          </Stack>

          <Divider />

          {submitted ? (
            <Alert severity="success">
              Your email client has been opened with a pre-filled deletion request. Please send
              the email to complete your request. If it did not open, email us directly at{' '}
              <strong>admin@misterfoodsafety.com</strong> with the subject{' '}
              <strong>"Account &amp; Data Deletion Request"</strong>.
            </Alert>
          ) : (
            <Stack component="form" onSubmit={handleSubmit} spacing={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Submit a deletion request
              </Typography>
              <TextField
                label="Your registered email address"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                error={!!error}
                helperText={error}
                fullWidth
                required
              />
              <Button
                type="submit"
                variant="contained"
                color="error"
                size="large"
                startIcon={<DeleteForeverIcon />}
              >
                Request Account Deletion
              </Button>
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Alternatively, email us directly at{' '}
                <Box
                  component="a"
                  href="mailto:admin@misterfoodsafety.com"
                  sx={{ color: 'primary.main', textDecoration: 'none' }}
                >
                  admin@misterfoodsafety.com
                </Box>
              </Typography>
            </Stack>
          )}

          <Divider />

          <Typography variant="caption" color="text.disabled" textAlign="center">
            &copy; {new Date().getFullYear()} Mister Food Safety (OPC) Private Limited. All rights reserved.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default DeleteAccountPage;
